import {
  useTransition,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import DropdownIcon from "@mui/icons-material/ArrowDropDown";
import CircleIcon from "@mui/icons-material/FiberManualRecord";
import {
  Tooltip,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  Checkbox,
  TextField,
} from "@mui/material";
import {
  useAnimationsEnabled,
  useDetailKey,
  useGetPageFromPath,
  useMidgardIdHubletPage,
  useSummaryGroup,
  useVisibleGroups,
  useWorkScheduleIdOnCurrentPage,
} from "../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import styled from "styled-components/macro";
import { useNavigate, useLocation } from "react-router";
import {
  blueButton,
  darkGrey,
  darkModeModalBackgroundColor,
  darkModeTextColor,
} from "constants/colors";
import {
  BREAKPOINT_TABLET,
  PNL_COLORS,
  useBreakpoints,
} from "constants/constants";
import { BREAKPOINT_MUI_NAV_SHRINK_8_PX } from "constants/constants";
import { useSelector } from "store/useSelector";
import { Link } from "react-router-dom";
import qs from "query-string";
import { ROTATE_GROUP } from "components/AutoRotatePages";
import useNavTitle from "./NavTitle";
import { useIsDarkMode } from "store/atoms";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { clearAllTrendChartBars } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import { useShowMachineSwitcherDropdownArrow } from "./topBarUtils";
import { animated, useSpring } from "@react-spring/web";
import {
  BREADCRUMBS_GAP_REMS,
  useBreadcrumbDropdowns,
} from "./useBreadcrumbDropdowns";

const DOT_SIZE = 10;
const DOT_MARGIN_RIGHT = 6;
const DOT_MARGIN_LEFT = DOT_SIZE + DOT_MARGIN_RIGHT;
const EXTRA_PADDING = 2; // extra px space for the left side of the dot
const MENU_ITEM_SIDE_PADDING =
  DOT_MARGIN_LEFT + DOT_MARGIN_RIGHT + EXTRA_PADDING;

const MachineSwitcherDropdownStyles = styled.h6`
  display: flex;
  justify-content: center;
  flex-grow: 1;
  .MuiMenu-paper {
    transform: translateY(4px) scale(1.2) !important;
  }
`;

export default function MachineSwitcherDropdown() {
  const {
    isGroupTilesPage,
    isReportsPage,
    isSettingsPage,
    isHelpPage,
    isTrendsPage,
    isCustomerTilesPage,
    isSnapshotsPage,
    isPowerBiPage,
  } = useGetPageFromPath();
  // on group tiles page, MachineSwitcherDropdown will never show or OR need to animate in (it only animates from tiles/timeline page),
  // so for better performance, don't render it on group tiles page etc
  const hide =
    isGroupTilesPage ||
    isTrendsPage ||
    isReportsPage ||
    isSettingsPage ||
    isHelpPage ||
    isCustomerTilesPage ||
    isSnapshotsPage ||
    isPowerBiPage;
  return hide ? null : <MachineSwitcherDropdownContent />;
}
function MachineSwitcherDropdownContent() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const navTitle = useNavTitle();
  const visibleGroups = useVisibleGroups();

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const pageInUrl = usePageInUrl();
  const [_isPending, startTransition] = useTransition();
  const [stale, setStale] = useState(false);
  const dispatch = useDispatch();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const { isTier0Page } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const ckey = useCustomerKey();
  const handleNavigateTo = useCallback(
    (group, processIdx) => {
      setStale(true);
      startTransition(() => {
        dispatch(clearAllTrendChartBars());

        const nextUrl = isTier0Page
          ? // on tier0 page, navigate to tier0 page with process=processName in search params
            `${pageInUrl}?process=${encodeURIComponent(
              group.processes[processIdx].name
            )}&workSchedule=${workScheduleId}`
          : midgardIdHubletPage
            ? `${ckey}/process/${group.processes[processIdx].midgardId}`
            : `${pageInUrl}/${group.summaryGroup}/${processIdx}?workSchedule=${workScheduleId}`;
        navigate(nextUrl);
        handleClose();
        setStale(false);
      });
    },
    [
      dispatch,
      isTier0Page,
      navigate,
      pageInUrl,
      workScheduleId,
      handleClose,
      midgardIdHubletPage,
      ckey,
    ]
  );
  /* when we switch pages, apply "stale" transparency styles to the now-stale dashboards */
  useEffect(() => {
    const dashboard = document.querySelector<HTMLDivElement>(
      ".dashboardPageContent"
    );
    if (!dashboard) {
      return;
    }
    if (stale) {
      dashboard.classList.add("stale-data");
    } else {
      dashboard.classList.remove("stale-data");
    }
    return () => {
      dashboard.classList.remove("stale-data");
    };
  }, [stale]);

  const isLargeNavbar = useMediaQuery(
    `(min-width: ${BREAKPOINT_MUI_NAV_SHRINK_8_PX}px)`
  );

  const pnlStatuses = useCurrentPnlStatuses();
  const { translate } = useFlightTranslation();

  const [
    {
      hubletView: { autoRotateBetweenProcesses },
    },
  ] = useFlightConfig();

  const [autoRotateMinutes, setAutoRotateMinutes] = useState(1);
  const { pathname, search } = useLocation();
  const isAutoRotating = useMemo(() => search.includes(ROTATE_GROUP), [search]);
  const [isDarkMode] = useIsDarkMode();

  const { showMSDropdown } = useBreadcrumbDropdowns();
  const showMachineSwitcherDropdownArrow =
    useShowMachineSwitcherDropdownArrow();
  const animationsEnabled = useAnimationsEnabled();
  const { isTabletOrLarger } = useBreakpoints();
  const springLeftRight = useSpring({
    ...(animationsEnabled
      ? {
          transform: `translateX(${
            showMSDropdown ? 0 : Math.min(500, 0.5 * innerWidth + 48 + 96)
          }px)`,
          marginLeft:
            showMSDropdown && !isTabletOrLarger
              ? `-${2 * BREADCRUMBS_GAP_REMS}rem`
              : "0rem",
        }
      : {}),
    opacity: showMSDropdown ? 1 : 0,
    config: showMSDropdown
      ? {
          /* default config */ tension: 170,
          friction: 26,
          mass: 1,
        }
      : {
          /* a bit slower on the way out */ tension: 100,
          friction: 30,
          mass: 1,
        },
  });
  const summaryGroup = useSummaryGroup();
  const detailKey = useDetailKey();
  const processGroup = useMemo(
    () => visibleGroups.find(g => g.summaryGroup === summaryGroup),
    [summaryGroup, visibleGroups]
  );
  const singleProcessName = useMemo(() => {
    if (midgardIdHubletPage) {
      return visibleGroups
        .flatMap(group => group.processes)
        .find(proc => proc.midgardId === midgardIdHubletPage)?.name;
    }
    return (
      processGroup?.processes[detailKey]?.name ??
      // on tier0 page process name is in the url search params under key "process"
      (isTier0Page && qs.parse(search).process)
    );
  }, [
    processGroup,
    detailKey,
    isTier0Page,
    search,
    midgardIdHubletPage,
    visibleGroups,
  ]);

  return (
    <animated.div style={springLeftRight} className="machineswitcher">
      <MachineSwitcherDropdownStyles
        className="name"
        data-component-name={"Machine Switcher Dropdown"}
        style={{
          pointerEvents: showMSDropdown ? "auto" : "none",
        }}
      >
        <Tooltip
          title={
            showMachineSwitcherDropdownArrow
              ? translate(`Switch_processes`)
              : ""
          }
        >
          <Button
            aria-controls="machine-switcher-menu"
            aria-haspopup="true"
            className="btnDropDown"
            onClick={
              showMachineSwitcherDropdownArrow ? handleClick : () => null
            }
            style={
              !showMachineSwitcherDropdownArrow ? { pointerEvents: "none" } : {}
            }
            endIcon={
              showMachineSwitcherDropdownArrow ? (
                <DropdownIcon className="dropdownArrow" />
              ) : undefined
            }
          >
            <div className="departmentName hubletPage">{singleProcessName}</div>
          </Button>
        </Tooltip>
        {isAutoRotating && (
          <AutoRotateEveryNMinutesStyles>
            Rotating pages every{" "}
            <TextField
              type="number"
              inputProps={{ min: 1, max: 20, step: 1 }}
              value={autoRotateMinutes}
              onChange={e => {
                const newMinutes = Number(e.target.value);
                setAutoRotateMinutes(newMinutes);
                const query = qs.stringify({
                  ...qs.parse(search),
                  [ROTATE_GROUP]: `${newMinutes}min`,
                });
                navigate(`${pathname}?${query}`, { replace: true });
              }}
            />
            min
          </AutoRotateEveryNMinutesStyles>
        )}
        <Menu
          id="machine-switcher-menu"
          anchorEl={anchorEl}
          keepMounted={false}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
          disableScrollLock={true}
          className={"machineSwitcherPaper"}
          slotProps={{
            paper: {
              style: {
                marginTop: -46,
                ...(isLargeNavbar
                  ? {
                      transform: `translateY(4px) scale(1.2)`,
                      ...(isDarkMode
                        ? {
                            background: darkModeModalBackgroundColor,
                            color: darkModeTextColor,
                          }
                        : {}),
                    }
                  : {}),
              },
            },
          }}
          MenuListProps={{
            style: {
              maxHeight: "75vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            },
          }}
        >
          {visibleGroups.map((group, groupIdx) => {
            if (groupIdx !== 0) {
              return null;
            } // * for now, only show the current group
            return [
              <MenuItem
                key={groupIdx}
                disabled={true}
                style={{
                  borderBottom: `1px solid ${darkGrey}`,
                  fontWeight: "bold",
                  paddingLeft: MENU_ITEM_SIDE_PADDING,
                  paddingRight: MENU_ITEM_SIDE_PADDING,
                  width: "100%",
                  justifyContent: "left",
                  ...(isLargeNavbar
                    ? { paddingTop: 4, marginTop: -4, paddingBottom: 6 }
                    : { paddingTop: 12, marginTop: -16, paddingBottom: 0 }),
                }}
              >
                {group.shortName}
              </MenuItem>,
              ...group.processes
                .sort((a, b) => {
                  if (group.processesSortOrder) {
                    return (
                      group.processesSortOrder.indexOf(a.id) -
                      group.processesSortOrder.indexOf(b.id)
                    );
                  }
                  return 0;
                })
                .map((process, processIdx) => (
                  <MenuItem
                    key={process.id}
                    disabled={process.name === navTitle}
                    onClick={() => handleNavigateTo(group, processIdx)}
                    style={{
                      paddingLeft: MENU_ITEM_SIDE_PADDING,
                      paddingRight: MENU_ITEM_SIDE_PADDING,
                      paddingTop: 6,
                      paddingBottom: 6,
                      width: "100%",
                      justifyContent: "left",
                      opacity: 1, // overwrite disabled items opacity to keep opacity 1 for pnl status dot
                    }}
                  >
                    <CircleIcon
                      style={{
                        fontSize: DOT_SIZE,
                        marginLeft: -DOT_MARGIN_LEFT,
                        marginRight: DOT_MARGIN_RIGHT,
                        color:
                          PNL_COLORS?.[pnlStatuses[groupIdx][process.id]] ||
                          "white",
                      }}
                    />
                    <div
                      style={
                        process.name === navTitle ? { opacity: 0.5 } : undefined
                      }
                    >
                      {process.name}
                    </div>
                  </MenuItem>
                )),
            ];
          })}
          {autoRotateBetweenProcesses && (
            <AutoRotatePagesInProcessGroupMenuItem
              {...{ handleClose, autoRotateMinutes }}
            />
          )}
        </Menu>
      </MachineSwitcherDropdownStyles>
    </animated.div>
  );
}
function useCurrentPnlStatuses() {
  const visibleGroups = useVisibleGroups();
  const allSegments = useSelector(state => state.legacy.segments);

  return useMemo(
    () =>
      visibleGroups.map(group =>
        group.processes.reduce(
          (acc, process) => {
            const processSegments = allSegments[process.id];
            const latestSegment = processSegments?.[processSegments.length - 1];
            const pnlCategory = latestSegment?.pnl?.category;
            return { ...acc, [process.id]: pnlCategory };
          },
          {} as { [processId: string]: string }
        )
      ),
    [allSegments, visibleGroups]
  );
}

/** returns the first part of the url including the ckey e.g. "<customer>/tier0" or "<customer>/grid" */
export function usePageInUrl() {
  const ckey = useCustomerKey();
  const { isGridLayoutPage, isOperatorPage, isTier0Page } =
    useGetPageFromPath();
  const { pathname } = useLocation();
  return useMemo(() => {
    const isDataDumpsDownloaderPage = pathname.includes("dataDumps");
    const pageName = isTier0Page
      ? "tier0"
      : isDataDumpsDownloaderPage
        ? "dataDumps"
        : isGridLayoutPage
          ? "grid"
          : isOperatorPage
            ? "operator"
            : "dashboard";
    return `/${ckey}/${pageName}`;
  }, [ckey, isGridLayoutPage, isOperatorPage, isTier0Page, pathname]);
}

function AutoRotatePagesInProcessGroupMenuItem({
  handleClose,
  autoRotateMinutes,
}) {
  const { pathname, search } = useLocation();
  const { checked, searchWithoutAutoRotate, searchWithAutoRotate } =
    useMemo(() => {
      const queryObj = qs.parse(search);
      const searchWithAutoRotate = qs.stringify({
        ...queryObj,
        rotategroup: `${autoRotateMinutes}min`,
      });
      const searchWithoutAutoRotate = qs.stringify({
        ...queryObj,
        rotategroup: undefined,
      });
      const checked = search.includes(ROTATE_GROUP);
      return {
        checked,
        searchWithoutAutoRotate,
        searchWithAutoRotate,
      };
    }, [autoRotateMinutes, search]);
  return (
    <StyledMenuItem>
      <Tooltip title="auto-rotate between pages in this process group every 1 minute">
        <Link
          to={`${pathname}?${
            checked ? searchWithoutAutoRotate : searchWithAutoRotate
          }`}
          onClick={handleClose}
        >
          Auto-rotate pages
          <Checkbox checked={checked} />
        </Link>
      </Tooltip>
    </StyledMenuItem>
  );
}
const AutoRotateEveryNMinutesStyles = styled.div`
  display: none;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    display: flex;
  }
  position: absolute;
  right: calc(50vw - 360px);
  gap: 10px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  &&&&& {
    .MuiTextField-root {
      .MuiInputBase-root {
        color: white;
        &:before {
          border-bottom: 1px solid rgba(255, 255, 255, 0.42);
        }
      }
    }
  }
`;
const StyledMenuItem = styled(MenuItem)`
  &&&&& {
    a {
      color: black;
      text-decoration: none;
    }
    .MuiSvgIcon-root {
      color: ${blueButton};
    }
  }
`;
